import { ThemeEvents, VariantUpdateEvent, VariantStockValidationEvent } from '@theme/events';
import { morph } from '@theme/morph';

class ProductInventory extends HTMLElement {
  
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateInventory);
    // Overrides the shopify provided stock status with custom T/NT variant logic
    document.addEventListener(ThemeEvents.variantStockValidation, this.#handleCustomStockValidation);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateInventory);
    document.removeEventListener(ThemeEvents.variantStockValidation, this.#handleCustomStockValidation);
  }

  /**
   * Overrides the shopify provided stock status with custom T/NT variant logic
   * @param {VariantStockValidationEvent} event - The variant stock validation event.
   */
  #handleCustomStockValidation = (event) => {
    if (event.detail.sourceId !== this.dataset.sectionId) return;
    if(event.detail.variantId == this.dataset.variantId || event.detail.swapVariantId == this.dataset.variantId) {
      const isAvailable = event.detail.available;
      if(!isAvailable) {
        this.#disable()
      }
      else {
        this.#enable();
      }
    }    
  }

  /**
   * Shows the inventory count element and hides our static validation text. 
   * Used when we've had to forcibly change the stock status of a variant due to custom T/NT logic
   * We can't reliably handle the inventory quantity count and have it persist so we show/hide it instead and let shopify handle it for products without T/NT sync issues
   */
  #enable() {
    this.querySelector(".product-inventory__text")?.classList.remove("hidden");
    this.querySelector(".product-inventory__custom-validation-text")?.classList.add("hidden");
    this.querySelector(".product-inventory__custom-validation-text")?.setAttribute("aria-hidden", "true");
    this.classList.remove("product-inventory--unavailable");
  }

  /**
   * Hides the inventory count element and shows our static validation text. 
   * Used when we've had to forcibly change the stock status of a variant due to custom T/NT logic
   * We can't reliably handle the inventory quantity count and have it persist so we show/hide it instead and let shopify handle it for products without T/NT sync issues
   */
  #disable() {
    this.querySelector(".product-inventory__text")?.classList.add("hidden");
    this.querySelector(".product-inventory__custom-validation-text")?.classList.remove("hidden");
    this.querySelector(".product-inventory__custom-validation-text")?.removeAttribute("aria-hidden");
    this.classList.add("product-inventory--unavailable");
  }

  /**
   * Updates the inventory.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateInventory = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newInventory = event.detail.data.html.querySelector('product-inventory');

    if (!newInventory) return;

    // Reset custom inventory display, morph seems to break interal context for bindings and que, likely replaces the element
    // Without explicitly running this the variant change listener for custom stock won't remove existing status'
    this.#enable()
    morph(this, newInventory, { childrenOnly: true });
  };
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
